import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GalleryAPI, UploadAPI, resolveImageUrl } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Confirm, EmptyState, Field, Modal, PageHeader, SearchInput, StatusPill, inputCls } from "@/components/admin/ui";

export const GALLERY_SECTIONS = [
  { id: "journey", num: "01", name: "01 THE JOURNEY" },
  { id: "moments", num: "02", name: "02 THE MOMENTS" },
  { id: "company", num: "03", name: "03 IN GOOD COMPANY" },
  { id: "kitchen", num: "04", name: "04 FROM THE KITCHEN" },
  { id: "plate", num: "05", name: "05 BEYOND THE PLATE" },
];

const empty = { imagePath: "", caption: "", section: "journey", title: "", displayOrder: 0 };

const fmt = (d) => {
  if (!d) return "—";
  const dateStr = d.includes("T") ? d.split("T")[0] : d;
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const GalleryAdmin = () => {
  const { log, user } = useAdmin();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [selectedSection, setSelectedSection] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [preview, setPreview] = useState("");
  const [errors, setErrors] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const { data: gallery = [], isLoading } = useQuery({ queryKey: ["gallery"], queryFn: () => GalleryAPI.getAll() });

  useEffect(() => {
    if (location.state?.add) {
      setForm(empty);
      setFileToUpload(null);
      setPreview("");
      setEditing("new");
    }
  }, [location.state]);

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      let imagePath = payload.imagePath;
      if (fileToUpload) {
        imagePath = await UploadAPI.uploadFile(fileToUpload, "gallery");
      }
      return GalleryAPI.create({ ...payload, imagePath, createdBy: user?.userId || 1 });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gallery"]);
      log("Gallery image added", form.caption || form.title);
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      let imagePath = payload.imagePath;
      if (fileToUpload) {
        imagePath = await UploadAPI.uploadFile(fileToUpload, "gallery");
      }
      return GalleryAPI.update(id, { ...payload, imagePath });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["gallery"]);
      log("Gallery image updated", form.caption || form.title);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: GalleryAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(["gallery"]);
      setConfirmDel(null);
    },
  });

  const rows = gallery.filter((g) => {
    const matchesSearch =
      (g.caption || "").toLowerCase().includes(q.toLowerCase()) ||
      (g.title || "").toLowerCase().includes(q.toLowerCase());
    const matchesSection =
      selectedSection === "all" || (g.section || "").toLowerCase() === selectedSection.toLowerCase();
    return matchesSearch && matchesSection;
  });

  const openEdit = (g) => {
    setForm({
      imagePath: g.imagePath,
      caption: g.caption || "",
      section: g.section || "journey",
      title: g.title || "",
      displayOrder: g.displayOrder ?? 0,
    });
    setPreview(resolveImageUrl(g.imagePath));
    setFileToUpload(null);
    setErrors({});
    setEditing(g.galleryId);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileToUpload(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const save = () => {
    const errs = {};
    if (!form.caption?.trim() && !form.title?.trim()) errs.caption = "Every frame needs a caption or title.";
    if (!form.imagePath && !fileToUpload) errs.image = "Choose a photograph.";
    if (!form.section) errs.section = "Select a gallery section.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    if (editing === "new") {
      createMutation.mutate(form);
    } else {
      updateMutation.mutate({ id: editing, payload: { ...form, isDeleted: false } });
    }
  };

  if (isLoading) return <div className="p-8 text-cream/50">Loading gallery...</div>;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div data-testid="admin-gallery">
      <PageHeader title="GALLERY" mal="ഗ്യാലറി" sub="The frames and memories of the house.">
        <SearchInput value={q} onChange={setQ} placeholder="Search captions & titles" />
        <BtnPrimary
          data-testid="add-gallery-btn"
          onClick={() => {
            setForm({ ...empty, section: selectedSection !== "all" ? selectedSection : "journey" });
            setPreview("");
            setFileToUpload(null);
            setErrors({});
            setEditing("new");
          }}
        >
          + Add Image
        </BtnPrimary>
      </PageHeader>

      {/* Section Filter Tabs */}
      <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        <button
          onClick={() => setSelectedSection("all")}
          className={`flex-shrink-0 px-3.5 py-1.5 font-body text-[10px] tracking-[0.2em] uppercase font-bold rounded border transition-all duration-300 ${
            selectedSection === "all"
              ? "border-brass bg-brass text-ink shadow-sm"
              : "border-cream/20 bg-bark/30 text-cream/70 hover:border-brass/50 hover:text-cream"
          }`}
        >
          ALL EXHIBITS ({gallery.length})
        </button>
        {GALLERY_SECTIONS.map((sec) => {
          const count = gallery.filter((g) => (g.section || "").toLowerCase() === sec.id).length;
          return (
            <button
              key={sec.id}
              onClick={() => setSelectedSection(sec.id)}
              className={`flex-shrink-0 px-3.5 py-1.5 font-body text-[10px] tracking-[0.2em] uppercase font-bold rounded border transition-all duration-300 ${
                selectedSection === sec.id
                  ? "border-brass bg-brass text-ink shadow-sm"
                  : "border-cream/20 bg-bark/30 text-cream/70 hover:border-brass/50 hover:text-cream"
              }`}
            >
              {sec.name} ({count})
            </button>
          );
        })}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="BARE WALLS"
          line="No frames match this filter. Add a photograph to this exhibit."
          actionLabel="+ Add Image"
          onAction={() => {
            setForm({ ...empty, section: selectedSection !== "all" ? selectedSection : "journey" });
            setPreview("");
            setFileToUpload(null);
            setErrors({});
            setEditing("new");
          }}
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((g) => (
            <figure key={g.galleryId} data-testid={`gallery-item-${g.galleryId}`} className={`group ${g.isDeleted ? "opacity-50" : ""}`}>
              <div className="overflow-hidden border border-cream/12 bg-[#151009] p-2 relative">
                <img
                  src={resolveImageUrl(g.imagePath)}
                  alt={g.caption || g.title}
                  className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <span className="absolute top-4 left-4 rounded px-2 py-0.5 text-[8.5px] uppercase tracking-wider font-semibold bg-ink/90 text-brass border border-brass/40 shadow">
                  {GALLERY_SECTIONS.find((s) => s.id === (g.section || "").toLowerCase())?.name || g.section}
                </span>
              </div>
              <figcaption className="mt-3 flex items-start justify-between gap-3">
                <div>
                  {g.title && (
                    <p className="font-body text-[10px] tracking-[0.25em] uppercase text-brass/90 font-semibold mb-0.5">
                      {g.title}
                    </p>
                  )}
                  <p className="font-display text-base italic text-cream">{g.caption || "No caption"}</p>
                  <p className="mt-1 font-body text-[9px] tracking-[0.25em] uppercase text-cream/40">
                    {g.createdByName || (g.createdBy ? `User ${g.createdBy}` : "System")} • {fmt(g.createdOn)}
                    {g.displayOrder > 0 && ` • Order: ${g.displayOrder}`}
                  </p>
                  {g.isDeleted && <StatusPill tone="terra">Deleted</StatusPill>}
                </div>
                <div className="flex shrink-0 gap-3 pt-1">
                  <button
                    data-testid={`gallery-edit-${g.galleryId}`}
                    onClick={() => openEdit(g)}
                    className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass"
                  >
                    Edit
                  </button>
                  {!g.isDeleted ? (
                    <button
                      data-testid={`gallery-delete-${g.galleryId}`}
                      onClick={() => setConfirmDel(g)}
                      className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra"
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      data-testid={`gallery-restore-${g.galleryId}`}
                      onClick={() => {
                        updateMutation.mutate({ id: g.galleryId, payload: { ...g, isDeleted: false } });
                        log("Gallery image restored", g.caption || g.title);
                      }}
                      className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/60 hover:text-brass"
                    >
                      Restore
                    </button>
                  )}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={editing === "new" ? "ADD IMAGE" : "EDIT IMAGE"} onClose={() => setEditing(null)}>
          <Field label="Photograph" error={errors.image}>
            <label className="flex min-h-48 cursor-pointer items-center justify-center border border-dashed border-cream/20 bg-bark/50 transition-colors hover:border-brass/50">
              {preview ? (
                <img src={preview} alt="Preview" className="max-h-72 w-auto max-w-full object-contain" />
              ) : (
                <span className="py-16 font-body text-[10px] tracking-[0.3em] uppercase text-cream/40">
                  Choose a photograph
                </span>
              )}
              <input
                data-testid="gallery-form-image"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          </Field>

          <div className="mt-5">
            <Field label="Gallery Section / Exhibit" error={errors.section}>
              <select
                data-testid="gallery-form-section"
                className={inputCls}
                value={form.section}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
              >
                {GALLERY_SECTIONS.map((sec) => (
                  <option key={sec.id} value={sec.id} className="bg-[#1c120c] text-cream">
                    {sec.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Title (e.g. MASTERY OVER FIRE)" error={errors.title}>
              <input
                data-testid="gallery-form-title"
                className={inputCls}
                value={form.title}
                placeholder="MASTERY OVER FIRE"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Caption / Subtitle" error={errors.caption}>
              <input
                data-testid="gallery-form-caption"
                className={inputCls}
                value={form.caption}
                placeholder="THE ART OF FLAME TOSSING & TEMPERING"
                onChange={(e) => setForm({ ...form, caption: e.target.value })}
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Display Order (Sequence)">
              <input
                data-testid="gallery-form-order"
                type="number"
                className={inputCls}
                value={form.displayOrder}
                placeholder="1"
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })}
              />
            </Field>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <BtnGhost onClick={() => setEditing(null)} disabled={isSaving}>
              Cancel
            </BtnGhost>
            <BtnPrimary data-testid="gallery-form-save" onClick={save} disabled={isSaving}>
              {isSaving ? "Saving..." : editing === "new" ? "Add Image" : "Save Changes"}
            </BtnPrimary>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Confirm
          testId="gallery-delete-confirm"
          title="REMOVE THIS MEMORY?"
          body={`“${confirmDel.caption || confirmDel.title}” will come down from the wall. You can hang it back later.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.galleryId);
            log("Gallery image removed", confirmDel.caption || confirmDel.title);
          }}
        />
      )}
    </div>
  );
};

export default GalleryAdmin;
